import type { NodeTree, TreeNode } from './tree.js';
import type { Allowance, NodeId, Permission } from './types.js';

/** Who is asking: a login plus the groups that login belongs to in this database. */
export interface Principal {
  login: string;
  groups: readonly string[];
}

/**
 * Effective allowance of one permission for one principal on one node.
 *
 * Rules (behavior matched to the reference implementation, see
 * docs/spec/ndb-permissions.md):
 * 1. On a single node, every entry whose selector matches the principal
 *    (its user, or one of its groups) is combined:
 *    strongAllow beats forbid ('deny'), forbid beats allow.
 * 2. A node with no matching entry inherits the parent's effective value.
 * 3. The permission is granted only for allow / strongAllow.
 */
export function effectiveAllowance(tree: NodeTree, nodeId: NodeId, who: Principal, permission: Permission): Allowance | null {
  for (const n of tree.ancestry(nodeId)) {
    const own = combineAtNode(n, who, permission);
    if (own) return own;
  }
  return null;
}

export function testPermission(tree: NodeTree, nodeId: NodeId, who: Principal, permission: Permission): boolean {
  const a = effectiveAllowance(tree, nodeId, who, permission);
  return a === 'allow' || a === 'strongAllow';
}

function combineAtNode(n: TreeNode, who: Principal, permission: Permission): Allowance | null {
  let result: Allowance | null = null;
  for (const e of n.perms) {
    if (e.permission !== permission) continue;
    const matches = e.kind === 'user' ? e.id === who.login : who.groups.includes(e.id);
    if (!matches) continue;
    result = merge(result, e.allowance);
  }
  return result;
}

function merge(current: Allowance | null, next: Allowance): Allowance {
  if (next === 'allow' && current === 'deny') return current;
  if (next === 'deny' && current === 'strongAllow') return current;
  return next;
}
